import { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Video, Book, Grid3X3, ChevronLeft, ChevronRight, MapPin, X, Bell, Clock, ExternalLink, MessageCircle, CheckCircle2 } from 'lucide-react';
import { CareModeViewer, type CareModeResourceType } from '@/components/care-recipient/care-mode-viewer';
import { OrientationPanel } from '@/components/care-recipient/orientation-panel';
import BreakButton from '@/components/care-recipient/break-button';
import CareRecipientVideos from './care-recipient-videos';
import CareRecipientMessenger from './care-recipient-messenger';
import { VisualWeekCalendar } from '@/components/reminders/visual-week-calendar';
import RectangularTimerReadOnly from '@/components/timer/RectangularTimerReadOnly';
import CircularTimer from '@/components/timer/CircularTimer';
import { useTimerStore } from '@/store/useTimerStore';
import {
  acknowledgeTimerCompletion,
  getTimerCompletionKey,
  hasAcknowledgedTimerCompletion,
  isValidTimerCompletionResource,
} from '@/lib/timer-completion';
import { formatReminderTime, formatReminderTimeRange } from '@/lib/reminder-time';
import {
  getDateKeyInTimeZone,
  getDateKeys,
  getReminderCountdown,
  reminderOccurrenceStart,
  reminderOccursOnDate,
} from '@shared/reminder-occurrence';
import type { RouteComponentProps } from 'wouter';
import { careTokenHeaders } from '@/lib/care-token';

type CareRecipientViewParams = {
  hubId: string;
};

type CareViewTab = 'timers' | 'schedules' | 'videos' | 'narratives' | 'choices' | 'locations' | 'rewards' | 'reminders' | 'messenger';

const CARE_VIEW_TABS = [
  { key: 'timers', label: 'Timers', icon: '/timers-icon.jpg' },
  { key: 'schedules', label: 'Schedules', icon: '/picture-schedule-icon.jpg' },
  { key: 'videos', label: 'Videos', icon: '/video-model-icon.png' },
  { key: 'narratives', label: 'Stories', icon: '/social-narratives-icon.jpg' },
  { key: 'choices', label: 'Choices', icon: '/choice-boards-icon.jpg' },
  { key: 'locations', label: 'Places', icon: '/location-prep-icon.jpg' },
  { key: 'rewards', label: 'Rewards', icon: '/rewards-icon.jpg' },
  { key: 'reminders', label: 'Reminders', icon: '/reminders-icon.jpg' },
  { key: 'messenger', label: 'Messages', icon: null },
] as const satisfies ReadonlyArray<{ key: CareViewTab; label: string; icon: string | null }>;

// ── Timer completion sound (mirrors hub-timers-fixed.tsx) ────────────────────
function playCompletionSound(soundType: string) {
  try {
    const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const sounds: Record<string, { freq: number; duration: number; type: OscillatorType }[]> = {
      chime:       [{ freq: 523, duration: 0.2, type: 'sine' }, { freq: 659, duration: 0.2, type: 'sine' }, { freq: 784, duration: 0.4, type: 'sine' }],
      bell:        [{ freq: 440, duration: 0.6, type: 'sine' }],
      celebration: [{ freq: 523, duration: 0.15, type: 'square' }, { freq: 659, duration: 0.15, type: 'square' }, { freq: 784, duration: 0.15, type: 'square' }, { freq: 1047, duration: 0.3, type: 'square' }],
      gentle:      [{ freq: 392, duration: 0.5, type: 'sine' }],
    };
    const notes = sounds[soundType] || sounds.chime;
    let time = audioCtx.currentTime;
    notes.forEach((note) => {
      const osc = audioCtx.createOscillator();
      const g   = audioCtx.createGain();
      osc.connect(g);
      g.connect(audioCtx.destination);
      osc.type = note.type;
      osc.frequency.value = note.freq;
      g.gain.value = 0.3;
      g.gain.exponentialRampToValueAtTime(0.001, time + note.duration);
      osc.start(time);
      osc.stop(time + note.duration);
      time += note.duration;
    });
  } catch (err) {
    console.warn('[CareView] playCompletionSound failed:', err);
  }
}

export default function CareRecipientView(props: RouteComponentProps<CareRecipientViewParams>) {
  const mountedRef = useRef(false);
  if (!mountedRef.current) {
    console.log('[CARE VIEW] mounted');
    mountedRef.current = true;
  }
  
  const hubIdFromRoute = props.params?.hubId;
  const hubIdFromQuery = new URLSearchParams(window.location.search).get('hubId');
  
  const hubId = hubIdFromRoute || hubIdFromQuery || '';
  
  const [hubName, setHubName] = useState<string>('Hub');
  const [activeTab, setActiveTab] = useState<CareViewTab>('timers');
  const careSwipeStart = useRef<{ x: number; y: number } | null>(null);

  // ── Timer completion overlay (mirrors hub-timers-fixed.tsx behaviour) ─────
  const [completionOverlay, setCompletionOverlay] = useState<{
    timerId: string;
    completionKey: string;
    timerTitle: string;
    completionSound: string | null;
    completionResourceType: string | null;
    completionResourceId: string | null;
  } | null>(null);
  const completedTimerIdsRef = useRef<Set<string>>(new Set());
  const pendingCareTimerCompletionsRef = useRef<Map<string, any>>(new Map());

  // Care Mode state (persisted to localStorage)
  const [careMode, setCareMode] = useState<boolean>(() => {
    try { return localStorage.getItem('careMode') === 'true'; } catch { return false; }
  });
  const [careModeType, setCareModeType] = useState<CareModeResourceType | null>(null);

  // Schedules
  const [schedules, setSchedules] = useState<any[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [completedStepIds, setCompletedStepIds] = useState<Set<string>>(new Set());
  const [scheduleCompletionOverlay, setScheduleCompletionOverlay] = useState<{
    scheduleId: string;
    scheduleTitle: string;
  } | null>(null);
  const completedScheduleIdsRef = useRef<Set<string>>(new Set());
  const pendingStepProgressRef = useRef<Set<string>>(new Set());

  // Narratives
  const [narratives, setNarratives] = useState<any[]>([]);
  const [selectedNarrative, setSelectedNarrative] = useState<any>(null);
  const [showNarrativeDialog, setShowNarrativeDialog] = useState(false);
  const [currentNarrativePageIndex, setCurrentNarrativePageIndex] = useState(0);

  // GPS coords for timezone-aware choice board filtering.
  // Care View MUST work without GPS — this is best-effort and non-blocking.
  // gpsRequestedRef ensures we only call getCurrentPosition ONCE per mount,
  // even if the component re-renders before the timeout fires.
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lng: number } | null>(null);
  const gpsRequestedRef = useRef(false);

  // Track the timezone the server last used so we can skip a redundant board
  // update when GPS produces the same timezone as the hub's configured one.
  const [timezoneInfo, setTimezoneInfo] = useState<{ used: string; source: string; autoTimezone: boolean } | null>(null);
  const activeTimezoneRef = useRef<string | null>(null);

  useEffect(() => {
    if (!navigator.geolocation || gpsRequestedRef.current) return;

    // Defer GPS request so the initial choice boards load (hub timezone) completes first.
    const timer = setTimeout(() => {
      gpsRequestedRef.current = true;          // one-shot: never request again this session
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (_err) => {
          // Permission denied, unavailable, or timed out.
          // Care View continues using hub.timezone — no retry, no UI change.
        },
        { timeout: 5000, maximumAge: 300000 }
      );
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Choice boards
  const [choiceBoards, setChoiceBoards] = useState<any[]>([]);
  const [selectedChoiceBoard, setSelectedChoiceBoard] = useState<any>(null);
  const [showChoiceBoardDialog, setShowChoiceBoardDialog] = useState(false);
  const [choiceItems, setChoiceItems] = useState<any[]>([]);
  const [loadingChoiceItems, setLoadingChoiceItems] = useState(false);
  const [selectedChoiceItemIds, setSelectedChoiceItemIds] = useState<number[]>([]);
  const [selectionPending, setSelectionPending] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [choiceSelectionError, setChoiceSelectionError] = useState<string | null>(null);
  const [linkedFromSchedule, setLinkedFromSchedule] = useState(false);
  const [linkedScheduleStepId, setLinkedScheduleStepId] = useState<string | null>(null);
  const [stepChoices, setStepChoices] = useState<Record<string, { image: string; label: string }>>(() => {
    try {
      const stored = localStorage.getItem(`stepChoices_${hubId}`);
      return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
  }); 

  useEffect(() => {
    try {
      if (Object.keys(stepChoices).length > 0) {
        localStorage.setItem(`stepChoices_${hubId}`, JSON.stringify(stepChoices));
      }
    } catch {}
  }, [stepChoices, hubId]);

  // Locations
  const [locations, setLocations] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadingLocation, setLoadingLocation] = useState(false);
  
  // Reminders
  const [reminders, setReminders] = useState<any[]>([]);
  const [selectedReminderDateKey, setSelectedReminderDateKey] = useState<string | null>(null);

  // Reward charts
  const [rewardCharts, setRewardCharts] = useState<any[]>([]);
  const [rewardCompletedStars, setRewardCompletedStars] = useState<Record<string, Record<string, number>>>({});

  // Sequential learning mode for location tags
  const [showLabels, setShowLabels] = useState(true);
  const [sequentialMode, setSequentialMode] = useState(false);
  const [currentSequenceIndex, setCurrentSequenceIndex] = useState(0);
  const [wrongTagShake, setWrongTagShake] = useState<number | null>(null);
  
  // Audio ref for playing recorded tag audio
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  // Play tag audio - prioritizes recorded audio over TTS
  // Supports both base64 data URLs and server URLs
  const playTagAudio = (tag: any) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    window.speechSynthesis?.cancel();
    
    const audioUrl = tag.audioUrl?.trim();
    const isDataUrl = audioUrl?.startsWith('data:');
    const isServerUrl = audioUrl && !isDataUrl && (audioUrl.startsWith('/') || audioUrl.startsWith('http'));
    
    console.log('[CareView] Tag clicked:', {
      id: tag.id,
      label: tag.label,
      audioUrlType: isDataUrl ? 'base64' : isServerUrl ? 'server-url' : 'none',
      audioUrlLength: audioUrl?.length || 0,
      hasValidAudio: !!(audioUrl)
    });
    
    // PRIORITY 1: Recorded audio (base64 data URL or server URL)
    if (audioUrl && (isDataUrl || isServerUrl)) {
      console.log('[CareView] Playing recorded audio for tag:', tag.label, 
        isDataUrl ? '(base64)' : '(server URL)');
      try {
        audioRef.current = new Audio();
        audioRef.current.src = audioUrl;
        audioRef.current.onerror = (e) => {
          console.error('[CareView] Audio playback error, falling back to TTS');
          if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(tag.description || tag.label);
            window.speechSynthesis.speak(utterance);
          }
        };
        audioRef.current.play().catch((err) => {
          console.error('[CareView] Failed to play tag audio:', err);
          if ('speechSynthesis' in window) {
            const utterance = new SpeechSynthesisUtterance(tag.description || tag.label);
            window.speechSynthesis.speak(utterance);
          }
        });
      } catch (err) {
        console.error('[CareView] Error creating Audio object:', err);
        if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(tag.description || tag.label);
          window.speechSynthesis.speak(utterance);
        }
      }
    } else {
      // PRIORITY 2: Fallback to TTS (NO click sound for semantic content)
      console.log('[CareView] No recorded audio, using TTS for tag:', tag.label);
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(tag.description || tag.label);
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utterance);
      }
    }
  };

  // Single ticking clock - ONE source of time updates
  const [now, setNow] = useState(Date.now());

  // Tick every second - this is the ONLY place time updates occur
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Track visibility for refetching data
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // Fetch authorized Hub/recipient identity through the existing Care View grant.
  useEffect(() => {
    if (!hubId) return;
    fetch(`/api/care/hub-profile?hubId=${encodeURIComponent(hubId)}`, {
      headers: careTokenHeaders(hubId),
      credentials: 'include',
    })
      .then(res => res.ok ? res.json() : { name: 'Hub' })
      .then(data => setHubName(data.careRecipient?.displayName || data.name || 'Hub'))
      .catch(() => setHubName('Hub'));
  }, [hubId]);

  // Load schedules from public endpoint
  useEffect(() => {
    if (!hubId) return;
    
    const loadSchedules = async () => {
      try {
        const response = await fetch(`/api/care/schedules?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
        if (response.ok) {
          const data = await response.json();
          const nextSchedules = Array.isArray(data) ? data : [];
          setSchedules(nextSchedules);
          setSelectedSchedule((current: any) => {
            if (!current) return current;
            const fresh = nextSchedules.find((schedule: any) => String(schedule.id) === String(current.id));
            if (!fresh) return current;
            setCompletedStepIds(new Set((fresh.completedStepIds ?? []).map(String)));
            return fresh;
          });
        }
      } catch {
        // Silent fail - no retry storm
      }
    };

    loadSchedules();
    const interval = setInterval(loadSchedules, 5000);
    return () => clearInterval(interval);
  }, [hubId]);

  const careViewSchedules = schedules;

  // Load reward charts from public endpoint
  useEffect(() => {
    if (!hubId) return;
    const loadRewardCharts = async () => {
      try {
        const response = await fetch(`/api/care/reward-charts?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
        if (response.ok) {
          const data = await response.json();
          setRewardCharts(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('[Care View] Error loading reward charts:', err);
      }
    };
    loadRewardCharts();
    const interval = setInterval(loadRewardCharts, 5000);
    return () => clearInterval(interval);
  }, [hubId]);

  // Load narratives from public endpoint
  useEffect(() => {
    if (!hubId) return;
    
    const loadNarratives = async () => {
      try {
        const response = await fetch(`/api/care/narratives?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
        if (response.ok) {
          const data = await response.json();
          setNarratives(Array.isArray(data) ? data : []);
        }
      } catch {
        // Silent fail - no retry storm
      }
    };

    loadNarratives();
    const interval = setInterval(loadNarratives, 5000);
    return () => clearInterval(interval);
  }, [hubId]);

  const careViewNarratives = narratives;

  // Load choice boards from public endpoint — send GPS coords for timezone-aware filtering
  useEffect(() => {
    if (!hubId) return;

    const loadChoiceBoards = async () => {
      try {
        const url = new URL(`/api/care/choice-boards`, window.location.origin);
        url.searchParams.set('hubId', hubId);
        if (gpsCoords) {
          url.searchParams.set('lat', String(gpsCoords.lat));
          url.searchParams.set('lng', String(gpsCoords.lng));
        }
        const response = await fetch(url.toString(), { headers: careTokenHeaders(hubId) });
        if (response.ok) {
          const data = await response.json();
          // API returns { boards, timezone_used, timezone_source }
          const boards = Array.isArray(data) ? data : (data.boards ?? []);
          const tzUsed: string | null = data.timezone_used ?? null;
          const tzSource: string | null = data.timezone_source ?? null;
          const tzAutoTimezone: boolean = data.auto_timezone ?? true;

          // Only update the timezone indicator when timezone actually changes —
          // this prevents a re-render of the indicator on every 5-second poll.
          if (tzUsed !== activeTimezoneRef.current) {
            activeTimezoneRef.current = tzUsed;
            if (tzUsed && tzSource) setTimezoneInfo({ used: tzUsed, source: tzSource, autoTimezone: tzAutoTimezone });
          }

          setChoiceBoards(boards);
        }
      } catch {
        // Silent fail - no retry storm
      }
    };

    loadChoiceBoards();
    const interval = setInterval(loadChoiceBoards, 5000);
    return () => clearInterval(interval);
  }, [hubId, gpsCoords]);

  const careViewChoiceBoards = choiceBoards;

  // Load locations from public endpoint
  useEffect(() => {
    if (!hubId) return;
    
    const loadLocations = async () => {
      try {
        const response = await fetch(`/api/care/locations?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
        if (response.ok) {
          const data = await response.json();
          setLocations(Array.isArray(data) ? data : []);
        }
      } catch {
        // Silent fail - no retry storm
      }
    };

    loadLocations();
    const interval = setInterval(loadLocations, 5000);
    return () => clearInterval(interval);
  }, [hubId]);

  const careViewLocations = locations;

  // Load reminders from public endpoint
  useEffect(() => {
    if (!hubId) return;
    
    const loadReminders = async () => {
      try {
        const response = await fetch(`/api/care/reminders?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
        if (response.ok) {
          const data = await response.json();
          setReminders(Array.isArray(data) ? data : []);
        }
      } catch {
        // Silent fail
      }
    };

    loadReminders();
    const interval = setInterval(loadReminders, 5000);
    return () => clearInterval(interval);
  }, [hubId]);

  // Timers from PUBLIC endpoint - refetch on window focus
  const { data: timerList = [], isLoading, refetch: refetchTimers } = useQuery<any[]>({
    queryKey: ['/api/care/timers', hubId],
    queryFn: async () => {
      if (!hubId) return [];
      console.log('[Care View] Fetching timers for hubId:', hubId);
       const response = await fetch(`/api/care/timers?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
      if (!response.ok) return [];
      const data = await response.json();
      console.log('[Care View] Timers fetched:', { hubId, count: data?.length });
      return data;
    },
    enabled: !!hubId,
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale so it refetches
  });

  // Timer state from PUBLIC endpoint - refetch on window focus
  const { data: serverTimerStates = {}, refetch: refetchTimerStates } = useQuery<Record<string, any>>({
    queryKey: ['/api/care/timer-state', hubId],
    queryFn: async () => {
      if (!hubId) return {};
       const response = await fetch(`/api/care/timer-state?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
      if (!response.ok) return {};
      return response.json();
    },
    enabled: !!hubId,
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale so it refetches
  });

  // ONE-TIME hydration: Seed timer store when timerList first loads
  // Uses ref to ensure hydration only happens once per component lifecycle
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return; // Already hydrated
    if (!timerList || timerList.length === 0) return; // No timers yet
    
    console.log('[Care View] One-time timer hydration, count:', timerList.length);
    hydratedRef.current = true;
    
    const store = useTimerStore.getState();
    
    timerList.forEach((timer: any) => {
      if (!timer.id) return;
      
      // Skip if store already has this timer
      if (store.timers[timer.id]) {
        console.log('[Care View] Store already has timer:', timer.id);
        return;
      }
      
      const serverState = serverTimerStates[timer.id];
      const isRunning = serverState?.status === 'running' || timer.status === 'running' || timer.isRunning === true;
      const isPaused = serverState?.status === 'paused' || timer.status === 'paused' || timer.isPaused === true;
      
      if (isRunning && !isPaused) {
        // Calculate endsAtMs
        let endsAtMs: number | undefined = serverState?.endsAtMs ?? timer.endsAtMs;
        if (!endsAtMs && (serverState?.endsAt || timer.endsAt)) {
          const parsed = new Date(serverState?.endsAt || timer.endsAt).getTime();
          if (Number.isFinite(parsed)) endsAtMs = parsed;
        }
        if (!endsAtMs) {
          const remaining = serverState?.timeRemaining ?? timer.remainingSeconds ?? timer.timeRemaining ?? timer.remainingTime;
          if (remaining != null && remaining > 0) {
            endsAtMs = Date.now() + remaining * 1000;
          }
        }
        
        if (endsAtMs) {
          console.log('[Care View] Hydrating running timer:', timer.id, 'endsAtMs:', endsAtMs);
          // Defer until the completion listener effect has mounted. This also
          // routes already-expired sessions through the single store path.
          setTimeout(() => store.hydrateRunningTimer(timer.id, endsAtMs!), 0);
        }
      }
    });
  }, [timerList, serverTimerStates]);

  // BroadcastChannel for real-time timer updates from hub
  // Refetch both timer list AND timer state when hub broadcasts an update
  useEffect(() => {
    const timerChannel = new BroadcastChannel('timer-updates');
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (data.type === 'timer-update' && data.timerId) {
        // DON'T refetch timer states here - the broadcast contains all needed info
        // The RectangularTimerReadOnly component will handle the broadcast directly
        // Refetching would overwrite the broadcast state with stale server data
        console.log('[Care View] Received timer broadcast (no refetch, let component handle):', data.timerId, data);
      }
      if (data.type === 'timer-visibility-change' && data.timerId) {
        console.log('[Care View] Received visibility change broadcast:', data.timerId, data);
        // Refetch timer list when visibility changes
        refetchTimers();
      }
    };
    timerChannel.addEventListener('message', handleMessage);
    return () => {
      timerChannel.removeEventListener('message', handleMessage);
      timerChannel.close();
    };
  }, [refetchTimers]);

  // ── Timer completion — show "All Done!" overlay in care view ────────────────
  // Listens for the same 'timer-completed' custom event fired by CircularTimer /
  // RectangularTimerReadOnly, plays the configured sound, fires confetti, and
  // shows the completion overlay — identical UX to hub-timers-fixed.tsx.
  useEffect(() => {
    const pendingCompletions = pendingCareTimerCompletionsRef.current;
    const showCompletion = (detail: any, timerData: any, completionKey: string) => {
      acknowledgeTimerCompletion(completionKey);
      const sound = timerData?.completionSound;
      if (sound && sound !== 'none') {
        playCompletionSound(sound);
      }

      try {
        import('canvas-confetti').then((mod) => {
          mod.default({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
        });
      } catch { /* non-fatal */ }

      setCompletionOverlay({
        timerId: String(detail.timerId),
        completionKey,
        timerTitle: timerData?.title || timerData?.name || 'Timer',
        completionSound: sound || null,
        completionResourceType: timerData?.completionResourceType || null,
        completionResourceId: timerData?.completionResourceId || null,
      });
    };

    const handleTimerCompleted = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const { timerId } = detail;
      const key = String(timerId);
      const completionKey = getTimerCompletionKey(detail);

      if (!completionKey || hasAcknowledgedTimerCompletion(completionKey)) return;
      if (completedTimerIdsRef.current.has(completionKey)) return;
      completedTimerIdsRef.current.add(completionKey);

      const timerData = timerList.find(
        (t: any) => String(t.id) === key || String(t.timerId) === key,
      );

      if (!timerData) {
        pendingCompletions.set(completionKey, detail);
        return;
      }

      showCompletion(detail, timerData, completionKey);
    };

    window.addEventListener('timer-completed', handleTimerCompleted);
    for (const [completionKey, detail] of pendingCompletions) {
      const timerData = timerList.find(
        (t: any) => String(t.id) === String(detail.timerId) || String(t.timerId) === String(detail.timerId),
      );
      if (timerData) {
        pendingCompletions.delete(completionKey);
        showCompletion(detail, timerData, completionKey);
      }
    }
    return () => window.removeEventListener('timer-completed', handleTimerCompleted);
  }, [timerList]);

  // Page Visibility API - refetch ALL data when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsPageVisible(isVisible);
      
      if (isVisible && hubId) {
        console.log('[Care View] Tab became visible - refreshing all data');
        setLastRefresh(Date.now());
        
        // Refetch React Query data (timers)
        refetchTimers();
        refetchTimerStates();
        
        // Refetch non-React Query data
        // Schedules
        fetch(`/api/care/schedules?hubId=${hubId}`, { headers: careTokenHeaders(hubId) })
          .then(res => res.ok ? res.json() : [])
          .then(data => setSchedules(Array.isArray(data) ? data : []))
          .catch(() => {});
        
        // Narratives
        fetch(`/api/care/narratives?hubId=${hubId}`, { headers: careTokenHeaders(hubId) })
          .then(res => res.ok ? res.json() : [])
          .then(data => setNarratives(Array.isArray(data) ? data : []))
          .catch(() => {});
        
        // Choice boards — response is { boards, timezone_used, timezone_source }
        fetch(`/api/care/choice-boards?hubId=${hubId}`, { headers: careTokenHeaders(hubId) })
          .then(res => res.ok ? res.json() : { boards: [] })
          .then(data => {
            const boards = Array.isArray(data) ? data : (data.boards ?? []);
            setChoiceBoards(boards);
          })
          .catch(() => {});
        
        // Locations
        fetch(`/api/care/locations?hubId=${hubId}`, { headers: careTokenHeaders(hubId) })
          .then(res => res.ok ? res.json() : [])
          .then(data => setLocations(Array.isArray(data) ? data : []))
          .catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hubId, refetchTimers, refetchTimerStates]);

  // Derive remaining time from endsAtMs - pure calculation, no setState
  // endsAtMs is the authoritative end timestamp from server (milliseconds)
  const getTimerState = (timer: any) => {
    const serverState = serverTimerStates[timer.id];
    const duration = serverState?.durationSeconds ?? serverState?.duration ?? timer.durationSeconds ?? timer.duration ?? 300;
    const status = serverState?.status ?? timer.status ?? 'paused';
    const timeRemaining = serverState?.remainingSeconds ?? serverState?.timeRemaining ?? timer.remainingSeconds ?? timer.timeRemaining ?? duration;
    
    // CRITICAL: Check isPaused from multiple sources as a guard
    const isPaused = status === 'paused' || serverState?.isPaused === true || timer.isPaused === true;
    const isRunning = status === 'running' && !isPaused;
    
    // Prefer endsAtMs (raw number) over endsAt (ISO string) to avoid Date parsing
    // CRITICAL: Only use endsAtMs for RUNNING timers - paused timers must NOT derive from endsAtMs
    let endsAtMs: number | null = null;
    if (!isPaused) {
      if (typeof serverState?.endsAtMs === 'number' && Number.isFinite(serverState.endsAtMs)) {
        endsAtMs = serverState.endsAtMs;
      } else if (typeof timer.endsAtMs === 'number' && Number.isFinite(timer.endsAtMs)) {
        endsAtMs = timer.endsAtMs;
      } else if (serverState?.endsAt) {
        const parsed = new Date(serverState.endsAt).getTime();
        if (Number.isFinite(parsed)) endsAtMs = parsed;
      } else if (timer.endsAt) {
        const parsed = new Date(timer.endsAt).getTime();
        if (Number.isFinite(parsed)) endsAtMs = parsed;
      }
    }
    
    // Safety assertion
    if (isPaused && (serverState?.endsAtMs || timer.endsAtMs)) {
      console.warn('[CareView] Paused timer has endsAtMs, ignoring:', timer.id);
    }
    
    let remaining: number;
    if (isRunning && endsAtMs != null) {
      // endsAtMs-driven countdown: remainingSeconds = max(0, ceil((endsAtMs - now) / 1000))
      remaining = Math.max(0, Math.ceil((endsAtMs - now) / 1000));
    } else {
      // Paused or not started - use static remaining value from server
      remaining = timeRemaining;
    }
    
    return {
      duration,
      remaining,
      isRunning,
      isPaused,
      status,
      endsAtMs,
    };
  };

  const renderTimer = (timer: any) => {
    const timerImage = timer.image || timer.imageUrl || '';
    const { duration, remaining, isRunning, isPaused, status, endsAtMs } = getTimerState(timer);

    // Check for rectangular timer - only check timerShape, not segments
    // Segments may not be loaded but we should still render as rectangular
    if (timer.timerShape === 'rectangular' || timer.shape === 'rectangular') {
      const currentTimer = { 
        ...timer, 
        remainingTime: remaining, 
        timeRemaining: remaining,
        remainingSeconds: remaining,
        isRunning, 
        isPaused,
        status,
        endsAtMs,
        durationSeconds: duration,
        // Ensure segments exist as array for the component
        segments: timer.segments || []
      };
      return (
        <RectangularTimerReadOnly 
          key={`care-${timer.id}`} 
          timer={currentTimer} 
        />
      );
    }
    
    return (
      <div key={`care-timer-${timer.id}`} className="flex flex-col items-center gap-3 p-4 bg-white rounded-xl shadow-lg">
        <h3 className="text-lg font-semibold text-gray-800">{timer.title || timer.name || 'Timer'}</h3>
        <CircularTimer 
          image={timerImage} 
          totalDuration={duration} 
          remainingTime={remaining} 
          mode={timer.coverMode || 'cover'} 
          size={220} 
          timerId={timer.id} 
        />
        {isRunning && (
          <Badge className="bg-green-500">Running</Badge>
        )}
        {isPaused && !isRunning && (
          <Badge className="bg-yellow-500">Paused</Badge>
        )}
      </div>
    );
  };

  const toggleCareMode = () => {
    setCareMode(prev => {
      const next = !prev;
      try { localStorage.setItem('careMode', String(next)); } catch {}
      return next;
    });
  };

  const closeCareMode = () => {
    setCareModeType(null);
    setSelectedSchedule(null);
    setSelectedNarrative(null);
    setSelectedChoiceBoard(null);
    setChoiceItems([]);
    setSelectedChoiceItemIds([]);
    setConfirmationSent(false);
  };

  const viewSchedule = (schedule: any) => {
    setSelectedSchedule(schedule);
    setCurrentStepIndex(0);
    setCompletedStepIds(new Set((schedule.completedStepIds ?? []).map(String)));
    if (careMode) {
      setCareModeType('schedule');
    } else {
      setShowScheduleDialog(true);
    }
  };

  const toggleStepComplete = async (stepId: string) => {
    if (!selectedSchedule?.id) return;
    const scheduleId = String(selectedSchedule.id);
    const requestKey = `${scheduleId}:${stepId}`;
    if (pendingStepProgressRef.current.has(requestKey)) return;
    pendingStepProgressRef.current.add(requestKey);

    const wasCompleted = completedStepIds.has(stepId);
    const nextCompleted = !wasCompleted;
    setCompletedStepIds(prev => {
      const next = new Set(prev);
      if (nextCompleted) next.add(stepId);
      else next.delete(stepId);
      return next;
    });

    try {
      const response = await fetch('/api/picture-schedules/step-progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...careTokenHeaders(hubId) },
        credentials: 'include',
        body: JSON.stringify({
          scheduleId,
          stepId,
          completed: nextCompleted,
        }),
      });
      if (!response.ok) throw new Error(`Step progress request failed: ${response.status}`);
      const result = await response.json();
      setCompletedStepIds(new Set((result.completedStepIds ?? []).map(String)));

      if (result.completed === true && !completedScheduleIdsRef.current.has(String(selectedSchedule.id))) {
        completedScheduleIdsRef.current.add(String(selectedSchedule.id));
        setSchedules(prev => prev.filter(schedule => String(schedule.id) !== String(selectedSchedule.id)));
        setShowScheduleDialog(false);
        setCareModeType(null);
        setScheduleCompletionOverlay({
          scheduleId: String(selectedSchedule.id),
          scheduleTitle: selectedSchedule.title || selectedSchedule.name || 'Schedule',
        });
      }
    } catch (error) {
      console.error('[CareView] Could not save picture schedule progress:', error);
      setCompletedStepIds(prev => {
        const next = new Set(prev);
        if (wasCompleted) next.add(stepId);
        else next.delete(stepId);
        return next;
      });
    } finally {
      pendingStepProgressRef.current.delete(requestKey);
    }
  };

  // Narrative view functions
  const viewNarrative = (narrative: any) => {
    setSelectedNarrative(narrative);
    setCurrentNarrativePageIndex(0);
    if (careMode) {
      setCareModeType('narrative');
    } else {
      setShowNarrativeDialog(true);
    }
  };

  const nextNarrativePage = () => {
    if (selectedNarrative?.pages && currentNarrativePageIndex < selectedNarrative.pages.length - 1) {
      setCurrentNarrativePageIndex(currentNarrativePageIndex + 1);
    }
  };

  const prevNarrativePage = () => {
    if (currentNarrativePageIndex > 0) {
      setCurrentNarrativePageIndex(currentNarrativePageIndex - 1);
    }
  };

  const currentNarrativePage = selectedNarrative?.pages?.[currentNarrativePageIndex];

  const nextStep = () => {
    if (selectedSchedule && currentStepIndex < selectedSchedule.steps.length - 1) setCurrentStepIndex(currentStepIndex + 1);
  };

  const prevStep = () => {
    if (currentStepIndex > 0) setCurrentStepIndex(currentStepIndex - 1);
  };

  const currentStep = selectedSchedule?.steps[currentStepIndex];

  // Location detail view functions
  const viewLocation = async (loc: any) => {
    setLoadingLocation(true);
    setCurrentImageIndex(0);
    try {
      const response = await fetch(`/api/care/locations/${loc.id}?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
      if (response.ok) {
        const data = await response.json();
        setSelectedLocation(data);
        setShowLocationDialog(true);
      } else {
        // Fallback to basic location data if detail fetch fails
        setSelectedLocation(loc);
        setShowLocationDialog(true);
      }
    } catch {
      // Fallback to basic location data
      setSelectedLocation(loc);
      setShowLocationDialog(true);
    } finally {
      setLoadingLocation(false);
    }
  };

  const nextImage = () => {
    if (selectedLocation?.images && currentImageIndex < selectedLocation.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  // Choice board view functions
  const viewChoiceBoard = async (board: any) => {
    setLoadingChoiceItems(true);
    setSelectedChoiceBoard(board);
    setSelectedChoiceItemIds([]);
    setSelectionPending(false);
    setConfirmationSent(false);
    if (careMode) {
      setCareModeType('choices');
    } else {
      setShowChoiceBoardDialog(true);
    }
    try {
      const response = await fetch(`/api/care/choice-items?choiceBoardId=${board.id}&hubId=${hubId}`, {
         headers: careTokenHeaders(hubId),
      });
      if (response.ok) {
        const data = await response.json();
        setChoiceItems(Array.isArray(data) ? data : []);
      } else {
        setChoiceItems([]);
      }
    } catch {
      setChoiceItems([]);
    } finally {
      setLoadingChoiceItems(false);
    }
  };

  const openLinkedChoiceBoard = async (boardId: string, stepId?: string) => {
    try {
      setLoadingChoiceItems(true);
      setLinkedFromSchedule(true);
      setLinkedScheduleStepId(stepId || null);
      const boardResponse = await fetch(`/api/care/choice-boards/${boardId}?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
      if (!boardResponse.ok) {
        setLinkedFromSchedule(false);
        setLoadingChoiceItems(false);
        return;
      }
      const board = await boardResponse.json();
      setSelectedChoiceBoard(board);
      if (careMode) {
        setCareModeType('choices');
      } else {
        setShowChoiceBoardDialog(true);
      }
      setSelectedChoiceItemIds([]);
      setSelectionPending(false);
      setConfirmationSent(false);
      // Pass viaSchedule=true so the server bypasses showInCareView for boards
      // that are genuinely linked to a Picture Schedule step in this hub.
       const itemsResponse = await fetch(`/api/care/choice-items?choiceBoardId=${boardId}&hubId=${hubId}&viaSchedule=true`, {
          headers: careTokenHeaders(hubId),
       });
      if (itemsResponse.ok) {
        const data = await itemsResponse.json();
        setChoiceItems(Array.isArray(data) ? data : []);
      } else {
        setChoiceItems([]);
      }
    } catch {
      setLinkedFromSchedule(false);
      setChoiceItems([]);
    } finally {
      setLoadingChoiceItems(false);
    }
  };

  const resourceTypeLabel = (type: string) => {
    switch (type) {
      case 'picture-schedule': return 'Picture Schedule';
      case 'social-narrative': return 'Social Narrative';
      case 'choice-board': return 'Choice Board';
      case 'location-preparation': return 'Location';
      case 'video-model': return 'Video';
      case 'reward-chart': return 'Reward Chart';
      default: return type;
    }
  };

  const resourceTypeIcon = (type: string) => {
    switch (type) {
      case 'picture-schedule': return Calendar;
      case 'social-narrative': return Book;
      case 'choice-board': return Grid3X3;
      case 'location-preparation': return MapPin;
      case 'video-model': return Video;
      case 'reward-chart': return Calendar;
      default: return Bell;
    }
  };

  const openLinkedResource = async (reminder: any) => {
    const rType = reminder.resourceType || reminder.resource_type;
    const rId = reminder.resourceId || reminder.resource_id;
    if (!rType || !rId || rType === 'custom') return;

    try {
      const response = await fetch(`/api/care/linked-resource/${rType}/${rId}?hubId=${hubId}`, { headers: careTokenHeaders(hubId) });
      if (!response.ok) return;
      const data = await response.json();

      if (rType === 'picture-schedule') {
        viewSchedule(data);
      } else if (rType === 'social-narrative') {
        viewNarrative(data);
      } else if (rType === 'choice-board') {
        viewChoiceBoard(data);
      } else if (rType === 'location-preparation') {
        setSelectedLocation(data);
        setCurrentImageIndex(0);
        setShowLocationDialog(true);
      }
    } catch (err) {
      console.error('[CARE] Error opening linked resource:', err);
    }
  };

  const openTimerCompletionResource = async () => {
    if (!completionOverlay) return;

    const { completionResourceType: type, completionResourceId: id } = completionOverlay;
    if (!isValidTimerCompletionResource(type, id)) return;

    acknowledgeTimerCompletion(completionOverlay.completionKey);
    setCompletionOverlay(null);
    await openLinkedResource({
      resourceType: type === 'schedule' ? 'picture-schedule' : 'social-narrative',
      resourceId: id,
    });
  };

  // Toggle a choice selection (for multi-select)
  const handleToggleChoice = (item: any) => {
    if (selectionPending || confirmationSent) return;
    
    const maxChoices = selectedChoiceBoard?.maxChoicesAllowed || 1;
    const isAlreadySelected = selectedChoiceItemIds.includes(item.id);
    
    // Play audio on toggle
    if (item.audioUrl) {
      try {
        const audio = new Audio(item.audioUrl);
        audio.preload = 'auto';
        audio.play();
      } catch (err) {
        console.error('[CARE AUDIO PLAY FAILED]', err);
      }
    } else if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(item.title);
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    }
    
    if (isAlreadySelected) {
      // Deselect
      setSelectedChoiceItemIds(prev => prev.filter(id => id !== item.id));
    } else {
      // Select - check if we're at max
      if (selectedChoiceItemIds.length >= maxChoices) {
        // If only 1 allowed, replace selection
        if (maxChoices === 1) {
          setSelectedChoiceItemIds([item.id]);
        }
        // Otherwise don't add more
        return;
      }
      setSelectedChoiceItemIds(prev => [...prev, item.id]);
    }
  };
  
  // Care View choices are sent once to the server for targeted fan-out.
  // Schedule-linked choices remain local schedule data.
  const handleConfirmSelections = async () => {
    if (selectionPending || confirmationSent || selectedChoiceItemIds.length === 0) return;
    if (!selectedChoiceBoard) return;

    if (linkedFromSchedule && linkedScheduleStepId) {
      const selectedItems = choiceItems.filter(item => selectedChoiceItemIds.includes(item.id));
      const choiceText = selectedItems.map(item => item.title).join(', ');
      const selectedItem = selectedItems[0];
      if (selectedItem) {
        const choiceImage = selectedItem.imageUrl || selectedItem.image || '';
        setStepChoices(prev => ({
          ...prev,
          [linkedScheduleStepId]: { image: choiceImage, label: choiceText },
        }));
      }
      setConfirmationSent(true);

      setTimeout(() => {
        setShowChoiceBoardDialog(false);
        setSelectedChoiceItemIds([]);
        setConfirmationSent(false);
        setLinkedFromSchedule(false);
        setLinkedScheduleStepId(null);
      }, 1500);
      return;
    }

    setSelectionPending(true);
    setChoiceSelectionError(null);
    try {
      const selectedItems = choiceItems.filter(item => selectedChoiceItemIds.includes(item.id));
      const choiceText = selectedItems.map(item => item.title).join(', ');
      const headers = {
        'Content-Type': 'application/json',
        ...careTokenHeaders(hubId),
        'x-care-view-request': 'true',
      };

      const notifyResponse = await fetch(`/api/messenger/${hubId}/choice-selection`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          choiceText,
          boardTitle: selectedChoiceBoard.title,
          choiceItemIds: selectedChoiceItemIds,
          choiceBoardId: selectedChoiceBoard.id,
          selectionSource: 'care_view',
          choiceImage: selectedItems[0]?.imageUrl || selectedItems[0]?.image || null,
          choiceImages: selectedItems
            .map((item: any) => item.imageUrl || item.image || null)
            .filter((image: string | null) => Boolean(image)),
          choices: selectedItems.map((item: any) => ({
            label: item.title,
            image: item.imageUrl || item.image || null,
          })),
        }),
      });
      const notifyData = await notifyResponse.json().catch(() => ({}));
      if (!notifyResponse.ok) {
        throw new Error(notifyData.message || notifyData.error || 'The choice could not be sent to eligible people.');
      }

      const selectionResponse = await fetch('/api/care/choice-selections', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          choiceBoardId: selectedChoiceBoard.id,
          selections: selectedChoiceItemIds,
        }),
      });
      if (!selectionResponse.ok) {
        console.error('[CHOICE SELECTION] Message was sent, but selection history could not be recorded');
      }

      setConfirmationSent(true);
      setTimeout(() => {
        setShowChoiceBoardDialog(false);
        setSelectedChoiceItemIds([]);
        setConfirmationSent(false);
      }, 2000);
    } catch (error) {
      console.error('[CHOICE SELECTION] Error:', error);
      setChoiceSelectionError(
        error instanceof Error ? error.message : 'The choice could not be sent to eligible people. Please try again.',
      );
    } finally {
      setSelectionPending(false);
    }
  };
  
  const needsConfirmation = true;
  
  const handleSelectChoice = (item: any) => {
    if (!selectedChoiceBoard) return;
    handleToggleChoice(item);
  };

  const currentImage = selectedLocation?.images?.[currentImageIndex];
  const currentImageTags = selectedLocation?.tags?.filter((t: any) => t.imageIndex === currentImageIndex) || [];
  
  // Reset sequential learning mode when image or location changes
  useEffect(() => {
    if (sequentialMode) {
      setCurrentSequenceIndex(0);
    }
  }, [currentImageIndex, selectedLocation?.id]);
  
  // Reset states when dialog closes
  useEffect(() => {
    if (!showLocationDialog) {
      setSequentialMode(false);
      setCurrentSequenceIndex(0);
      setShowLabels(true);
    }
  }, [showLocationDialog]);
  
  // Simple sound utility for feedback
  const playSound = (type: 'click' | 'success' | 'error') => {
    // Uses Web Audio API for simple feedback sounds
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      if (type === 'click') {
        osc.frequency.value = 600;
        gain.gain.value = 0.1;
      } else if (type === 'success') {
        osc.frequency.value = 800;
        gain.gain.value = 0.15;
      } else {
        osc.frequency.value = 200;
        gain.gain.value = 0.1;
      }
      
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } catch {
      // Audio not available, silent fail
    }
  };

  // Show loading state only when hubId is missing
  if (!hubId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50">
        <div className="text-center">
          <p className="text-gray-600">No hub ID provided. Use ?hubId=...</p>
        </div>
      </div>
    );
  }

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-blue-50">Loading...</div>;

  // ── Derived tab status indicators ────────────────────────────────────────
  const hasRunningTimer = timerList.some((timer: any) => {
    const state = serverTimerStates[timer.id];
    return (state?.status === 'running') || (timer.status === 'running');
  });

  const hasAvailableReward = rewardCharts.some((chart: any) => {
    const tasks: any[] = chart.tasks || [];
    const stars: Record<string, number> = chart.completedStars || {};
    return tasks.some((task: any) => (stars[task.id] || 0) < 7);
  });

  const currentDateTime = new Date(now);
  const scheduleTimezone = reminders.find((reminder: any) => reminder.scheduleTimezone)?.scheduleTimezone
    || Intl.DateTimeFormat().resolvedOptions().timeZone
    || 'America/Chicago';
  const currentDateKey = getDateKeyInTimeZone(currentDateTime, scheduleTimezone);
  const currentWeekDateKeys = getDateKeys(currentDateKey, 7);
  const nextActivityDateKeys = getDateKeys(currentDateKey, 14);
  const reminderOccurrences = nextActivityDateKeys.flatMap((dateKey) =>
    reminders
      .filter((reminder: any) => reminderOccursOnDate(reminder, dateKey))
      .map((reminder: any) => ({
        reminder,
        dateKey,
        start: reminderOccurrenceStart(reminder, dateKey, scheduleTimezone),
      })),
  );

  // The existing card remains unchanged; only its visibility window now uses
  // the actual occurrence instant in the Hub's timezone.
  const nextActivityOccurrence = reminderOccurrences
    .filter(({ start }) => {
      const startMs = start.getTime();
      const nowMs = currentDateTime.getTime();
      const previewStartMs = startMs - 15 * 60 * 1000;
      const currentMinuteEndMs = startMs + 60 * 1000;
      return nowMs >= previewStartMs && nowMs < currentMinuteEndMs;
    })
    .sort((a, b) => a.start.getTime() - b.start.getTime())[0] ?? null;
  const nextReminder = nextActivityOccurrence?.reminder ?? null;

  const nowMins = currentDateTime.getHours() * 60 + currentDateTime.getMinutes();
  const hasUpcomingReminder = reminders.some((r: any) => {
    if (!r.reminderTime) return false;
    const [h, m] = r.reminderTime.split(':').map(Number);
    const diff = (h * 60 + m) - nowMins;
    return diff >= 0 && diff <= 60;
  });

  const fmtTime = (t: string) => {
    if (!t) return '';
    const [hStr, mStr] = t.split(':');
    const h = parseInt(hStr, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${mStr} ${ampm}`;
  };

  const nextReminderResType = nextReminder?.resourceType || nextReminder?.resource_type;
  const nextReminderResId   = nextReminder?.resourceId   || nextReminder?.resource_id;
  const hasLinkedResource   = !!nextReminderResType && nextReminderResType !== 'custom' && !!nextReminderResId;
  const activeTabIndex = CARE_VIEW_TABS.findIndex((tab) => tab.key === activeTab);
  const moveCareTab = (direction: -1 | 1) => {
    const nextIndex = Math.min(
      CARE_VIEW_TABS.length - 1,
      Math.max(0, activeTabIndex + direction),
    );
    setActiveTab(CARE_VIEW_TABS[nextIndex].key);
  };
  const handleCareSwipeStart = (event: React.TouchEvent) => {
    if (!careMode) return;
    careSwipeStart.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
    };
  };
  const handleCareSwipeEnd = (event: React.TouchEvent) => {
    const start = careSwipeStart.current;
    careSwipeStart.current = null;
    if (!careMode || !start) return;
    const deltaX = event.changedTouches[0].clientX - start.x;
    const deltaY = event.changedTouches[0].clientY - start.y;
    if (Math.abs(deltaX) < 60 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    moveCareTab(deltaX < 0 ? 1 : -1);
  };
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Sticky orientation panel — full width, always visible */}
      <OrientationPanel
        hubName={hubName}
        reminders={reminders}
        careMode={careMode}
        onToggleCareMode={toggleCareMode}
      />

      {/* pb-28 on mobile ensures the fixed "I Need a Break" button (≈84px tall from bottom)
          never overlaps the last card; sm+ uses pb-8 since the button is less obtrusive. */}
      <div className={`${careMode ? 'max-w-6xl px-3 sm:px-6' : 'max-w-4xl px-4'} mx-auto pt-4 pb-28 sm:pb-8`}>

        {/* ── Next Activity Bar ──────────────────────────────────────────────── */}
        {nextReminder && !careMode && (
          <div className="bg-white rounded-2xl shadow-md mb-4 overflow-hidden border border-blue-100">
            {/* Accent stripe */}
            <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-400 to-purple-400" />

            <div className="flex gap-4 px-5 py-4">
              {/* ── Thumbnail ── */}
              <div className="shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-blue-50 border border-blue-100 flex items-center justify-center self-center">
                {nextReminder.coverImage ? (
                  <img
                    src={nextReminder.coverImage}
                    alt={nextReminder.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  (() => {
                    const FallbackIcon = hasLinkedResource
                      ? resourceTypeIcon(nextReminderResType!)
                      : Bell;
                    return (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100">
                        <FallbackIcon className="w-10 h-10 text-blue-400" />
                      </div>
                    );
                  })()
                )}
              </div>

              {/* ── Text + button ── */}
              <div className="flex-1 min-w-0 flex flex-col justify-between gap-3">
                {/* Label + time */}
                <div className="flex items-center gap-2">
                  <span className="text-blue-600 text-xs font-semibold tracking-wide uppercase flex items-center gap-1">
                    <span className="leading-none">▶</span> Next Activity
                  </span>
                  <span className="ml-auto text-sm font-medium text-gray-400 tabular-nums shrink-0">
                    {fmtTime(nextReminder.reminderTime)}
                  </span>
                </div>

                {/* Activity name */}
                <p className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight line-clamp-2">
                  {nextReminder.title}
                </p>

                {/* Action button */}
                <button
                  onClick={() => {
                    if (hasLinkedResource) {
                      openLinkedResource(nextReminder);
                    } else {
                      setActiveTab('reminders');
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white rounded-xl py-3.5 px-4 text-lg font-bold shadow-sm transition-all duration-150 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300"
                >
                  {(() => {
                    if (hasLinkedResource) {
                      const IconComp = resourceTypeIcon(nextReminderResType!);
                      return (
                        <>
                          <IconComp className="w-5 h-5 shrink-0" />
                          Open {resourceTypeLabel(nextReminderResType!)}
                        </>
                      );
                    }
                    return (
                      <>
                        <Bell className="w-5 h-5 shrink-0" />
                        View Reminder
                      </>
                    );
                  })()}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ────────────────────────────────────────────────────────────────────── */}

        {/* Tabs */}
        <div className={`${careMode ? 'rounded-2xl border-2 border-blue-100' : 'rounded-lg'} bg-white shadow-lg overflow-hidden mb-6`}>
          <div className={careMode ? 'flex gap-3 overflow-x-auto p-3 snap-x snap-mandatory' : 'grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9'}>
            {CARE_VIEW_TABS.map(tab => {
              const isActive = activeTab === tab.key;

              // Per-tab state flags
              const timerAlert   = tab.key === 'timers'    && hasRunningTimer    && !isActive;
              const rewardAlert  = tab.key === 'rewards'   && hasAvailableReward && !isActive;
              const reminderAlert= tab.key === 'reminders' && hasUpcomingReminder && !isActive;

              // Build class names
              let tabClass = careMode
                ? 'relative flex min-h-[112px] min-w-[116px] snap-center flex-col items-center justify-center gap-2 rounded-2xl border-2 px-4 py-3 text-lg font-bold transition-all '
                : 'relative flex flex-col items-center gap-1 px-2 py-3 font-medium text-xs transition-colors ';
              if (isActive) {
                tabClass += careMode ? 'border-blue-600 bg-blue-600 text-white shadow-md' : 'bg-blue-600 text-white';
              } else if (timerAlert) {
                tabClass += `text-blue-700 bg-blue-50 care-tab-timer-active ${careMode ? 'border-blue-200' : ''}`;
              } else if (rewardAlert) {
                tabClass += `text-amber-700 bg-amber-50 ${careMode ? 'border-amber-200' : ''}`;
              } else if (reminderAlert) {
                tabClass += `text-orange-700 care-tab-reminder-pulse ${careMode ? 'border-orange-200' : ''}`;
              } else {
                tabClass += `text-gray-600 hover:text-gray-800 hover:bg-gray-50 ${careMode ? 'border-gray-200' : ''}`;
              }

              // Accessible badge/dot for non-active state alerts
              const showBlueDot    = timerAlert;
              const showGoldBadge  = rewardAlert;
              const showOrangeDot  = reminderAlert;

              // Tooltip text
              const title = timerAlert    ? 'Timer is running'
                          : rewardAlert   ? 'Stars to earn!'
                          : reminderAlert ? 'Reminder coming up'
                          : tab.label;

              return (
                <button
                  key={tab.key}
                  data-testid={`care-tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={tabClass}
                  title={title}
                  aria-label={title}
                >
                  <div className="relative">
                    {tab.icon ? (
                      <img src={tab.icon} alt="" className={`${careMode ? 'h-14 w-14' : 'h-8 w-8'} object-contain rounded`} />
                    ) : (
                      <MessageCircle className={careMode ? 'h-14 w-14' : 'h-8 w-8'} />
                    )}

                    {/* Blue dot — timer running */}
                    {showBlueDot && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-white" aria-hidden="true" />
                    )}

                    {/* Gold star badge — reward available */}
                    {showGoldBadge && (
                      <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full bg-amber-400 border border-amber-200 text-[8px] font-bold text-white leading-none" aria-hidden="true">★</span>
                    )}

                    {/* Orange dot — upcoming reminder */}
                    {showOrangeDot && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-orange-400 border-2 border-white" aria-hidden="true" />
                    )}
                  </div>

                  <span className={`
                    ${timerAlert    ? 'text-blue-700 font-semibold' : ''}
                    ${rewardAlert   ? 'text-amber-700 font-semibold' : ''}
                    ${reminderAlert ? 'text-orange-600 font-semibold' : ''}
                  `}>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {careMode && (
          <div className="mb-3 flex items-center justify-between px-2 text-blue-700">
            <button
              type="button"
              onClick={() => moveCareTab(-1)}
              disabled={activeTabIndex === 0}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow disabled:opacity-25"
              aria-label="Previous section"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
            <div className="text-center">
              <p className="text-2xl font-bold">{CARE_VIEW_TABS[activeTabIndex]?.label}</p>
              <p className="text-sm text-blue-500">Swipe to explore</p>
            </div>
            <button
              type="button"
              onClick={() => moveCareTab(1)}
              disabled={activeTabIndex === CARE_VIEW_TABS.length - 1}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow disabled:opacity-25"
              aria-label="Next section"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          </div>
        )}

        <div
          data-testid="care-view-content"
          data-view-mode={careMode ? 'care' : 'standard'}
          className={`${careMode ? 'rounded-3xl bg-white p-4 shadow-sm sm:p-8' : 'p-6'}`}
          style={{ minHeight: careMode ? '420px' : '200px', touchAction: 'pan-y' }}
          onTouchStart={handleCareSwipeStart}
          onTouchEnd={handleCareSwipeEnd}
        >
          {activeTab === 'timers' ? (
            <div className={`grid grid-cols-1 ${careMode ? 'md:grid-cols-2 gap-8' : 'sm:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
              {timerList.length === 0 ? (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-2xl">⏱️</span>
                  </div>
                  <p>No timers available for this hub.</p>
                </div>
              ) : (
                timerList.map((timer: any) => renderTimer(timer))
              )}
            </div>
          ) : activeTab === 'schedules' ? (
            (() => {
              const todayStr2 = new Date().toISOString().split('T')[0];
              const prepSchedules = reminders.filter((r: any) => {
                if (!r.preparationEnabled || r.resourceType !== 'picture-schedule' || !r.resourceId) return false;
                const eventDate = r.startDate || r.dueDate;
                if (!eventDate) return false;
                const daysUntil = Math.round((new Date(eventDate + 'T00:00:00').getTime() - new Date(todayStr2 + 'T00:00:00').getTime()) / 86400000);
                return daysUntil >= 0 && daysUntil <= (r.preparationDays || 0);
              }).map((r: any) => ({ reminder: r, resource: careViewSchedules.find((s: any) => String(s.id) === String(r.resourceId)) })).filter((x: any) => x.resource);
              return (
                <div className="space-y-6">
                  {prepSchedules.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-0.5 flex-1 bg-gradient-to-r from-amber-200 to-transparent" />
                        <span className="text-xs font-bold tracking-widest text-amber-600 uppercase">Preparation Resources</span>
                        <div className="h-0.5 flex-1 bg-gradient-to-l from-amber-200 to-transparent" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {prepSchedules.map(({ reminder, resource }: any) => (
                          <div key={reminder.id} className="relative">
                            <div className="absolute -top-2 left-3 z-10">
                              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">Preparation for {reminder.title}</span>
                            </div>
                            <Card className="cursor-pointer border-amber-200 pt-3" onClick={() => viewSchedule(resource)}>
                              <CardHeader className="p-0"><img src={resource.steps?.[0]?.image || ''} alt={resource.title} className="w-full h-48 object-cover rounded-t-lg" /></CardHeader>
                              <CardContent className="p-4"><CardTitle>{resource.title}</CardTitle><p className="text-gray-600 text-sm">{resource.description}</p></CardContent>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{careViewSchedules.map((schedule: any) => (
                    <Card
                      key={schedule.id}
                      data-testid={`care-schedule-${schedule.id}`}
                      className="cursor-pointer"
                      onClick={() => viewSchedule(schedule)}
                    >
                      <CardHeader className="p-0"><img src={schedule.steps?.[0]?.image || ''} alt={schedule.title} className="w-full h-48 object-cover rounded-t-lg" /></CardHeader>
                      <CardContent className="p-4"><CardTitle>{schedule.title}</CardTitle><p className="text-gray-600 text-sm">{schedule.description}</p></CardContent>
                    </Card>
                  ))}</div>
                </div>
              );
            })()
          ) : activeTab === 'videos' ? (
            <CareRecipientVideos hubId={hubId} />
          ) : activeTab === 'narratives' ? (
            (() => {
              const todayStr3 = new Date().toISOString().split('T')[0];
              const prepNarratives = reminders.filter((r: any) => {
                if (!r.preparationEnabled || r.resourceType !== 'social-narrative' || !r.resourceId) return false;
                const eventDate = r.startDate || r.dueDate;
                if (!eventDate) return false;
                const daysUntil = Math.round((new Date(eventDate + 'T00:00:00').getTime() - new Date(todayStr3 + 'T00:00:00').getTime()) / 86400000);
                return daysUntil >= 0 && daysUntil <= (r.preparationDays || 0);
              }).map((r: any) => ({ reminder: r, resource: careViewNarratives.find((n: any) => String(n.id) === String(r.resourceId)) })).filter((x: any) => x.resource);
              return (
                <div className="space-y-6">
                  {prepNarratives.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-0.5 flex-1 bg-gradient-to-r from-amber-200 to-transparent" />
                        <span className="text-xs font-bold tracking-widest text-amber-600 uppercase">Preparation Resources</span>
                        <div className="h-0.5 flex-1 bg-gradient-to-l from-amber-200 to-transparent" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {prepNarratives.map(({ reminder, resource }: any) => (
                          <div key={reminder.id} className="relative">
                            <div className="absolute -top-2 left-3 z-10">
                              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">Preparation for {reminder.title}</span>
                            </div>
                            <Card className="cursor-pointer border-amber-200 pt-3 hover:shadow-lg transition-shadow" onClick={() => viewNarrative(resource)}>
                              <CardHeader className="p-0"><img src={resource.coverImage || resource.pages?.[0]?.image || ''} alt={resource.title} className="w-full h-48 object-cover rounded-t-lg" /></CardHeader>
                              <CardContent className="p-4"><CardTitle>{resource.title}</CardTitle><p className="text-gray-600 text-sm">{resource.description}</p><Button className="w-full mt-3" variant="outline">Read Story</Button></CardContent>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{careViewNarratives.map((n: any) => (
                    <Card key={n.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => viewNarrative(n)}>
                      <CardHeader className="p-0"><img src={n.coverImage || n.pages?.[0]?.image || ''} alt={n.title} className="w-full h-48 object-cover rounded-t-lg" /></CardHeader>
                      <CardContent className="p-4"><CardTitle>{n.title}</CardTitle><p className="text-gray-600 text-sm">{n.description}</p><Button className="w-full mt-3" variant="outline">Read Story</Button></CardContent>
                    </Card>
                  ))}</div>
                </div>
              );
            })()
          ) : activeTab === 'choices' ? (
            (() => {
              // Format IANA timezone into a readable label, e.g. "America/Chicago" → "Central Time"
              const tzLabel = (tz: string | undefined) => {
                if (!tz) return tz ?? '';
                const map: Record<string, string> = {
                  'America/New_York': 'Eastern Time',
                  'America/Chicago': 'Central Time',
                  'America/Denver': 'Mountain Time',
                  'America/Los_Angeles': 'Pacific Time',
                  'America/Anchorage': 'Alaska Time',
                  'Pacific/Honolulu': 'Hawaii Time',
                  'America/Puerto_Rico': 'Atlantic Time',
                };
                return map[tz] ?? tz;
              };

              const todayStr4 = new Date().toISOString().split('T')[0];
              const prepChoices = reminders.filter((r: any) => {
                if (!r.preparationEnabled || r.resourceType !== 'choice-board' || !r.resourceId) return false;
                const eventDate = r.startDate || r.dueDate;
                if (!eventDate) return false;
                const daysUntil = Math.round((new Date(eventDate + 'T00:00:00').getTime() - new Date(todayStr4 + 'T00:00:00').getTime()) / 86400000);
                return daysUntil >= 0 && daysUntil <= (r.preparationDays || 0);
              }).map((r: any) => ({ reminder: r, resource: careViewChoiceBoards.find((b: any) => String(b.id) === String(r.resourceId)) })).filter((x: any) => x.resource);
              return (
                <div className="space-y-6">
                  {timezoneInfo && (
                    <p className="text-xs text-muted-foreground text-right select-none">
                      Using {tzLabel(timezoneInfo.used)}
                      {timezoneInfo.source === 'gps'
                        ? ' (device location)'
                        : !timezoneInfo.autoTimezone
                          ? ' (fixed setting)'
                          : timezoneInfo.source === 'hub' || timezoneInfo.source === 'default'
                            ? ' (default)'
                            : ''}
                    </p>
                  )}
                  {prepChoices.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-0.5 flex-1 bg-gradient-to-r from-amber-200 to-transparent" />
                        <span className="text-xs font-bold tracking-widest text-amber-600 uppercase">Preparation Resources</span>
                        <div className="h-0.5 flex-1 bg-gradient-to-l from-amber-200 to-transparent" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {prepChoices.map(({ reminder, resource }: any) => (
                          <div key={reminder.id} className="relative">
                            <div className="absolute -top-2 left-3 z-10">
                              <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">Preparation for {reminder.title}</span>
                            </div>
                            <Card className="cursor-pointer border-amber-200 pt-3 hover:shadow-lg transition-shadow" onClick={() => viewChoiceBoard(resource)}>
                              <CardHeader className="p-0">{resource.coverImageUrl ? <img src={resource.coverImageUrl} alt={resource.title} className="w-full h-48 object-cover rounded-t-lg" /> : <div className="w-full h-48 bg-gray-100 rounded-t-lg flex items-center justify-center"><span className="text-4xl">📋</span></div>}</CardHeader>
                              <CardContent className="p-4"><CardTitle>{resource.title}</CardTitle><p className="text-gray-600 text-sm">{resource.description}</p><Button className="w-full mt-3" variant="outline">Make a Choice</Button></CardContent>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{careViewChoiceBoards.map((b: any) => (
                    <Card key={b.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => viewChoiceBoard(b)}>
                      <CardHeader className="p-0">{(b.coverImageUrl) ? <img src={b.coverImageUrl} alt={b.title} className="w-full h-48 object-cover rounded-t-lg" /> : <div className="w-full h-48 bg-gray-100 rounded-t-lg flex items-center justify-center"><span className="text-4xl">📋</span></div>}</CardHeader>
                      <CardContent className="p-4"><CardTitle>{b.title}</CardTitle><p className="text-gray-600 text-sm">{b.description}</p><Button className="w-full mt-3" variant="outline">Make a Choice</Button></CardContent>
                    </Card>
                  ))}</div>
                </div>
              );
            })()
          ) : activeTab === 'locations' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {careViewLocations.length === 0 ? (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-2xl">📍</span>
                  </div>
                  <p>No locations available for this hub.</p>
                </div>
              ) : (
                careViewLocations.map(loc => (
                  <Card key={loc.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => viewLocation(loc)}>
                    <CardHeader className="p-0">
                      <img 
                        src={loc.imageUrl || '/placeholder-location.png'} 
                        alt={loc.name} 
                        className="w-full h-48 object-cover rounded-t-lg" 
                      />
                    </CardHeader>
                    <CardContent className="p-4">
                      <CardTitle>{loc.name}</CardTitle>
                      {loc.subcategory && (
                        <Badge className="mb-2">{loc.subcategory}</Badge>
                      )}
                      {loc.description && (
                        <p className="text-gray-600 text-sm mb-2">{loc.description}</p>
                      )}
                      {loc.address && (
                        <p className="text-gray-500 text-xs">{loc.address}{loc.city ? `, ${loc.city}` : ''}{loc.state ? `, ${loc.state}` : ''}</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          ) : activeTab === 'rewards' ? (
            <div className="space-y-6">
              {rewardCharts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-2xl">⭐</span>
                  </div>
                  <p>No reward charts available for this hub.</p>
                </div>
              ) : (
                rewardCharts.map((chart: any) => {
                  const chartStars = chart.completedStars || {};
                  return (
                    <div key={chart.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                      <div className="bg-blue-600 text-white p-4">
                        <h2 className="text-2xl font-bold">{chart.title}</h2>
                        {(chart.rewardLabel || chart.rewardDescription) && (
                          <p className="text-blue-100 mt-1">Reward: {chart.rewardDescription || chart.rewardLabel}</p>
                        )}
                      </div>
                      <div className="bg-white border-2 border-gray-200">
                        <div className="grid grid-cols-8 gap-0 bg-gray-100 border-b border-gray-200">
                          <div className="p-3 border-r border-gray-200 text-center font-semibold text-gray-700 text-sm">Tasks</div>
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                            <div key={day} className={`p-3 text-center font-semibold text-gray-700 text-sm ${index < 6 ? 'border-r border-gray-200' : ''}`}>
                              {day}
                            </div>
                          ))}
                        </div>
                        {(chart.tasks || []).map((task: any) => (
                          <div key={task.id} className="grid grid-cols-8 gap-0 border-b border-gray-200">
                            <div className="p-4 border-r border-gray-200 bg-white">
                              <div className="flex flex-col items-center gap-2">
                                {task.imageUrl ? (
                                  <img src={task.imageUrl} alt={task.name} className="w-12 h-12 object-cover rounded" />
                                ) : (
                                  <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                                    <span className="text-xl">⭐</span>
                                  </div>
                                )}
                                <span className="text-xs font-medium text-center">{task.name}</span>
                              </div>
                            </div>
                            {[1, 2, 3, 4, 5, 6, 7].map((day, dayIndex) => {
                              const isCompleted = (chartStars[task.id] || 0) >= day;
                              return (
                                <div key={`${task.id}-${day}`} className={`p-4 bg-white flex items-center justify-center ${dayIndex < 6 ? 'border-r border-gray-200' : ''}`}>
                                  <button
                                    onClick={() => {
                                      const current = chartStars[task.id] || 0;
                                      const newVal = current >= day ? day - 1 : day;
                                      const updatedStars = { ...chartStars, [task.id]: newVal };
                                      setRewardCharts(prev => prev.map(c => 
                                        c.id === chart.id ? { ...c, completedStars: updatedStars } : c
                                      ));
                                      fetch(`/api/care/reward-charts/${chart.id}/completions`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', ...careTokenHeaders(hubId) },
                                        body: JSON.stringify({ completedStars: updatedStars }),
                                      }).catch(err => console.error('Error updating completion:', err));
                                    }}
                                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                                      isCompleted
                                        ? 'bg-yellow-400 border-yellow-400'
                                        : 'border-dashed border-gray-300 hover:border-gray-500'
                                    }`}
                                  >
                                    {isCompleted && <span className="text-white text-sm">⭐</span>}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                        {chart.rewardImageUrl && (
                          <div className="bg-yellow-100 border-t-2 border-yellow-300 p-4">
                            <div className="flex items-center gap-4">
                              <img src={chart.rewardImageUrl} alt="Reward" className="w-16 h-16 object-cover rounded" />
                              <span className="font-bold text-yellow-800">{chart.rewardDescription || chart.rewardLabel || 'Reward'}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : activeTab === 'reminders' ? (
            (() => {
              const eventEmoji: Record<string, string> = {
                birthday: '🎂', holiday: '🎄', doctor: '🏥', school: '🏫',
                travel: '✈️', celebration: '🎉', family: '👨‍👩‍👧',
              };
              const countdownReminders = reminders
                .filter((reminder: any) =>
                  reminder.showDaysUntil === true || reminder.show_days_until === true,
                )
                .map((reminder: any) => ({
                  reminder,
                  countdown: getReminderCountdown(
                    { ...reminder, startDate: reminder.startDate || reminder.dueDate },
                    currentDateTime,
                    scheduleTimezone,
                  ),
                }))
                .filter((entry): entry is {
                  reminder: any;
                  countdown: { dateKey: string; daysUntil: number; label: string };
                } => Boolean(entry.countdown))
                .sort((a, b) => a.countdown.daysUntil - b.countdown.daysUntil);

              const occursForScheduleDate = (reminder: any, dateKey: string) => {
                if (!reminder.startDate && !reminder.dueDate) return dateKey === currentDateKey;
                return reminderOccursOnDate(
                  { ...reminder, startDate: reminder.startDate || reminder.dueDate },
                  dateKey,
                );
              };

              const weeklySchedule = currentWeekDateKeys.map((dateKey) => {
                const isToday = dateKey === currentDateKey;
                const isPastDay = dateKey < currentDateKey;
                const entries = reminders
                  .filter((reminder: any) => occursForScheduleDate(reminder, dateKey))
                  .map((reminder: any) => ({
                    reminder,
                    dateKey,
                    start: reminderOccurrenceStart(
                      reminder,
                      dateKey,
                      scheduleTimezone,
                    ),
                  }))
                  .filter(({ reminder, start }) => {
                    // Today is an active queue: past reminders disappear even
                    // if they were never marked done. Completed reminders remain
                    // visible so the weekly view can show their completion state.
                    // Prior days remain visible but subdued so the week still
                    // reads as a complete shape.
                    if (!isToday) return true;
                    if (reminder.completedToday === true) return true;
                    return start.getTime() + 60 * 1000 > currentDateTime.getTime();
                  })
                  .sort((a, b) => {
                    const timeDiff = a.start.getTime() - b.start.getTime();
                    return timeDiff || a.reminder.title.localeCompare(b.reminder.title);
                  });

                return { dateKey, isToday, isPastDay, entries };
              });

              const markReminderDone = async (reminder: any, occurrenceStart: Date) => {
                if (occurrenceStart.getTime() > currentDateTime.getTime()) return;

                // Optimistic: update the reminder in state immediately
                setReminders(prev => prev.map((r: any) =>
                  r.id === reminder.id ? { ...r, completedToday: true } : r
                ));
                try {
                  const response = await fetch('/api/care/reminder-completions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...careTokenHeaders(hubId) },
                    body: JSON.stringify({
                      reminderId: String(reminder.id),
                      hubId,
                      reminderTitle: reminder.title,
                    }),
                  });
                  if (!response.ok) throw new Error('Reminder completion was rejected');
                } catch { /* non-fatal — hub will sync on next poll */ }
              };

              const ReminderCard = ({
                reminder,
                dateKey,
                isPastDay,
                occurrenceStart,
              }: {
                reminder: any;
                dateKey: string;
                isPastDay: boolean;
                occurrenceStart: Date;
              }) => {
                const isToday = dateKey === currentDateKey;
                const isDone = isToday && reminder.completedToday === true;
                const isDue = occurrenceStart.getTime() <= currentDateTime.getTime();
                const canMarkDone = isToday && !isPastDay && !isDone && isDue;
                const reminderResourceType = reminder.resourceType || reminder.resource_type;
                const reminderResourceId = reminder.resourceId || reminder.resource_id;
                const hasLinkedResource = reminderResourceType &&
                  reminderResourceType !== 'custom' &&
                  reminderResourceId;
                const countdown = reminder.showDaysUntil === true || reminder.show_days_until === true
                  ? getReminderCountdown(
                      { ...reminder, startDate: reminder.startDate || reminder.dueDate },
                      currentDateTime,
                      scheduleTimezone,
                    )
                  : null;
                const countdownLabel = countdown?.dateKey === dateKey ? countdown.label : null;
                const fallbackIcon = reminder.showAsBigEvent
                  ? (eventEmoji[reminder.eventIcon] || '📅')
                  : '🔔';

                return (
                  <Card className={`overflow-hidden border transition-shadow hover:shadow-md ${
                    isDone
                      ? 'border-green-200 bg-green-50/80'
                      : isPastDay
                        ? 'border-gray-200 bg-gray-50 opacity-60'
                        : 'border-blue-100 bg-white'
                  }`}>
                    <div className="flex gap-4 p-3 sm:p-4">
                      <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 overflow-hidden rounded-xl border border-blue-100 bg-blue-50 flex items-center justify-center">
                        {reminder.coverImage ? (
                          <img
                            src={reminder.coverImage}
                            alt={reminder.title}
                            className="w-full h-full object-cover"
                            onError={(event) => {
                              (event.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100 text-3xl">
                            {fallbackIcon}
                          </div>
                        )}
                      </div>

                      <CardContent className="min-w-0 flex-1 p-0">
                        <div className="flex items-start gap-2">
                          <CardTitle className={`text-lg leading-tight ${isDone ? 'line-through text-muted-foreground' : 'text-gray-900'}`}>
                            {reminder.title}
                          </CardTitle>
                          {isDone && <CheckCircle2 className="w-5 h-5 shrink-0 text-green-600" />}
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-sm font-semibold text-blue-700">
                          <Clock className="w-4 h-4 shrink-0" />
                           <span>{reminder.reminderTime ? formatReminderTimeRange(reminder) : 'All day'}</span>
                        </div>
                        {countdownLabel && (
                          <p
                            data-testid={`reminder-countdown-${reminder.id}`}
                            className="mt-1 text-sm font-semibold text-purple-700"
                          >
                            {countdownLabel}
                          </p>
                        )}
                        {reminder.description && (
                          <p className="mt-2 line-clamp-2 text-sm text-gray-600">{reminder.description}</p>
                        )}

                        <div className="mt-3 flex flex-wrap gap-2">
                          {hasLinkedResource && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={() => openLinkedResource(reminder)}
                            >
                              {(() => {
                                const IconComp = resourceTypeIcon(reminderResourceType);
                                return <IconComp className="w-4 h-4" />;
                              })()}
                              <span>Open {resourceTypeLabel(reminderResourceType)}</span>
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                          )}
                          {!isPastDay && !isDone && (
                            <Button
                              size="sm"
                              disabled={!canMarkDone}
                              className="flex items-center gap-2 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-500"
                              onClick={() => markReminderDone(reminder, occurrenceStart)}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              {canMarkDone ? 'Mark as Done' : 'Available at ' + (reminder.reminderTime ? formatReminderTime(reminder.reminderTime) : 'start')}
                            </Button>
                          )}
                          {isDone && (
                            <span className="inline-flex items-center gap-1 text-sm font-semibold text-green-700">
                              Done today
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                );
              };

              const selectedDayKey = selectedReminderDateKey || currentDateKey;
              const selectedDay = weeklySchedule.find((day) => day.dateKey === selectedDayKey)
                || weeklySchedule[0];
              const selectedDayDate = selectedDay
                ? new Date(`${selectedDay.dateKey}T12:00:00Z`)
                : new Date();

              return (
                <div className="space-y-5">
                  {countdownReminders.length > 0 && (
                    <section
                      data-testid="care-reminder-countdowns"
                      className="rounded-2xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50 p-4 sm:p-5"
                    >
                      <div className="mb-3 flex items-center gap-2">
                        <span className="text-xl" aria-hidden="true">📅</span>
                        <div>
                          <h2 className="text-lg font-extrabold text-purple-900">Coming Up</h2>
                          <p className="text-sm font-medium text-purple-700">
                            Important dates to look forward to
                          </p>
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {countdownReminders.map(({ reminder, countdown }) => (
                          <div
                            key={String(reminder.id)}
                            data-testid={`care-reminder-countdown-${reminder.id}`}
                            className="flex items-center gap-3 rounded-xl border border-purple-200 bg-white p-3 shadow-sm"
                          >
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-purple-100 text-2xl">
                              {reminder.coverImage ? (
                                <img
                                  src={reminder.coverImage}
                                  alt=""
                                  aria-hidden="true"
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                eventEmoji[reminder.eventIcon] || '📅'
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-base font-extrabold text-purple-900">
                                {countdown.label}
                              </p>
                              <p className="truncate text-sm font-medium text-slate-700">
                                {reminder.title}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  <div className="flex items-center gap-3">
                    <div className="h-1 flex-1 rounded-full bg-gradient-to-r from-blue-500 to-indigo-300" />
                    <span className="text-sm font-bold uppercase tracking-[0.18em] text-blue-700">
                      Week at a glance
                    </span>
                    <div className="h-1 flex-1 rounded-full bg-gradient-to-l from-blue-500 to-indigo-300" />
                  </div>

                  <VisualWeekCalendar
                    days={weeklySchedule.map(({ dateKey, isToday, isPastDay, entries }) => ({
                      dateKey,
                      isToday,
                      isPastDay,
                      occurrences: entries.map(({ reminder }) => {
                        const countdown = (
                          reminder.showDaysUntil === true || reminder.show_days_until === true
                        )
                          ? getReminderCountdown(
                              { ...reminder, startDate: reminder.startDate || reminder.dueDate },
                              currentDateTime,
                              scheduleTimezone,
                            )
                          : null;

                        return {
                          id: `${reminder.id}-${dateKey}`,
                          title: reminder.title,
                          coverImage: reminder.coverImage,
                          reminderTime: reminder.reminderTime,
                          endTime: reminder.endTime,
                          eventEndDate: reminder.eventEndDate,
                          startDate: reminder.startDate || reminder.dueDate,
                          countdownLabel: countdown?.dateKey === dateKey ? countdown.label : null,
                          isCompleted: dateKey === currentDateKey && reminder.completedToday === true,
                          kind: 'reminder' as const,
                        };
                      }),
                    }))}
                    selectedDateKey={selectedDayKey}
                    onDaySelect={setSelectedReminderDateKey}
                    emptyLabel="Nothing scheduled"
                  />

                  {selectedDay && (
                    <section
                      data-testid="care-selected-reminder-details"
                      className="rounded-2xl border-2 border-blue-200 bg-blue-50/60 p-4 sm:p-5"
                    >
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <h2 className="text-xl font-extrabold text-blue-900">
                            {selectedDay.isToday ? "Today · " : ""}
                            {new Intl.DateTimeFormat("en-US", {
                              timeZone: scheduleTimezone,
                              weekday: "long",
                              month: "long",
                              day: "numeric",
                            }).format(selectedDayDate)}
                          </h2>
                          <p className="text-sm font-medium text-blue-700">
                            {selectedDay.isToday
                              ? "Reminders for today"
                              : selectedDay.isPastDay
                                ? "Earlier this week"
                                : "Coming up"}
                          </p>
                        </div>
                        <span className="rounded-full bg-blue-600 px-3 py-1 text-sm font-bold text-white">
                          {selectedDay.entries.length}
                        </span>
                      </div>

                      {selectedDay.entries.length > 0 ? (
                        <div className="space-y-3">
                          {selectedDay.entries.map((entry) => (
                            <ReminderCard
                              key={`${entry.reminder.id}-${entry.dateKey}`}
                              reminder={entry.reminder}
                              dateKey={entry.dateKey}
                              isPastDay={selectedDay.isPastDay}
                              occurrenceStart={entry.start}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-blue-200 bg-white/70 p-6 text-center text-sm text-blue-600">
                          <Bell className="mx-auto mb-2 h-5 w-5 opacity-60" />
                          {selectedDay.isToday ? "Nothing else scheduled today" : "Nothing scheduled"}
                        </div>
                      )}
                    </section>
                  )}
                </div>
              );
            })()
          ) : activeTab === 'messenger' ? (
            <CareRecipientMessenger hubId={hubId} />
          ) : null}
        </div>
      </div>

      {/* Schedule Detail Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">📋</span>
              {selectedSchedule?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedSchedule && (
            <div className="space-y-3">
              {selectedSchedule.description && (
                <p className="text-gray-600">{selectedSchedule.description}</p>
              )}
              {completedStepIds.size > 0 && (
                <div className="text-sm text-gray-500">
                  {completedStepIds.size} of {selectedSchedule.steps?.length || 0} steps completed
                </div>
              )}
              {selectedSchedule.steps && selectedSchedule.steps.length > 0 ? (
                <div className="space-y-3">
                  {selectedSchedule.steps.map((step: any, idx: number) => {
                    const stepKey = step.id || `step-${idx}`;
                    const isCompleted = completedStepIds.has(String(stepKey));
                    const stepChoice = stepChoices[String(stepKey)];
                    const displayImage = stepChoice?.image || step.image;
                    const choiceLabel = stepChoice?.label;
                    return (
                      <div
                        key={stepKey}
                        className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                          isCompleted
                            ? 'bg-green-50 border-green-200'
                            : 'bg-white border-gray-200 hover:border-primary/30'
                        }`}
                      >
                        <button
                           data-testid={`picture-schedule-step-toggle-${stepKey}`}
                          onClick={() => toggleStepComplete(String(stepKey))}
                          className={`flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isCompleted
                              ? 'bg-green-500 border-green-500 text-white'
                              : 'border-gray-300 text-gray-300 hover:border-primary hover:text-primary'
                          }`}
                        >
                          {isCompleted ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <span className="text-sm font-bold">{idx + 1}</span>
                          )}
                        </button>
                        {displayImage && (
                          <img
                            src={displayImage}
                            alt={step.label || step.title}
                            className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-semibold ${isCompleted ? 'line-through text-gray-400' : ''}`}>
                            {step.label || step.title}
                          </h4>
                          {step.notes && (
                            <p className={`text-sm mt-1 ${isCompleted ? 'text-gray-400' : 'text-gray-600'}`}>
                              {step.notes}
                            </p>
                          )}
                          {choiceLabel && (
                            <p className="text-sm mt-1 text-purple-600 font-medium">
                              Chosen: {choiceLabel}
                            </p>
                          )}
                          {step.linkedChoiceBoardId && !isCompleted && !choiceLabel && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="mt-2 text-purple-600 border-purple-300 hover:bg-purple-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                openLinkedChoiceBoard(step.linkedChoiceBoardId, String(stepKey));
                              }}
                            >
                              <Grid3X3 className="h-4 w-4 mr-1" />
                              Open Choices
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No steps in this schedule yet.</p>
              )}
              {selectedSchedule.steps?.length > 0 && completedStepIds.size === selectedSchedule.steps.length && (
                <div className="text-center py-4">
                  <p className="text-green-600 font-semibold text-lg">All done! Great job!</p>
                  <Button className="mt-2" onClick={() => setShowScheduleDialog(false)}>Close</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Narrative Reader Dialog */}
      <Dialog open={showNarrativeDialog} onOpenChange={setShowNarrativeDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Book className="h-5 w-5 text-blue-600" />
              {selectedNarrative?.title || 'Story'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedNarrative && (
            <div className="space-y-4">
              {selectedNarrative.pages && selectedNarrative.pages.length > 0 ? (
                <>
                  {/* Current Page Image */}
                  <div className="relative">
                    <img 
                      src={currentNarrativePage?.image || ''} 
                      alt={`Page ${currentNarrativePageIndex + 1}`}
                      className="w-full h-64 md:h-80 object-contain bg-gray-100 rounded-lg"
                    />
                  </div>
                  
                  {/* Page Text */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-lg text-center text-gray-800">
                      {currentNarrativePage?.text || 'No text for this page'}
                    </p>
                  </div>
                  
                  {/* Page Navigation */}
                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={prevNarrativePage}
                      disabled={currentNarrativePageIndex === 0}
                      className="flex items-center gap-2"
                    >
                      <ChevronLeft className="h-4 w-4" /> Previous
                    </Button>
                    
                    <span className="text-sm text-gray-500">
                      Page {currentNarrativePageIndex + 1} of {selectedNarrative.pages.length}
                    </span>
                    
                    <Button
                      variant={currentNarrativePageIndex === selectedNarrative.pages.length - 1 ? "default" : "outline"}
                      onClick={currentNarrativePageIndex === selectedNarrative.pages.length - 1 
                        ? () => setShowNarrativeDialog(false)
                        : nextNarrativePage
                      }
                      className="flex items-center gap-2"
                    >
                      {currentNarrativePageIndex === selectedNarrative.pages.length - 1 ? (
                        'Done'
                      ) : (
                        <>Next <ChevronRight className="h-4 w-4" /></>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>This story has no pages yet.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Location Detail Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              {selectedLocation?.name || 'Location'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingLocation ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : selectedLocation && (
            <div className="space-y-4">
              {/* Labels and Learning Mode Controls */}
              {currentImageTags.length > 0 && (
                <div className="flex gap-2 mb-2">
                  <Button
                    variant={showLabels ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowLabels(!showLabels)}
                    className="text-xs"
                  >
                    {showLabels ? "Labels On" : "Labels Off"}
                  </Button>
                  <Button
                    variant={sequentialMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setSequentialMode(!sequentialMode);
                      setCurrentSequenceIndex(0);
                      if (!sequentialMode && currentImageTags.length > 0) {
                        const sortedTags = [...currentImageTags].sort((a: any, b: any) => 
                          (a.displayOrder || 0) - (b.displayOrder || 0)
                        );
                        if ('speechSynthesis' in window) {
                          const utterance = new SpeechSynthesisUtterance(`Learning mode. Find: ${sortedTags[0]?.label}`);
                          window.speechSynthesis.cancel();
                          window.speechSynthesis.speak(utterance);
                        }
                      }
                    }}
                    className="text-xs"
                  >
                    {sequentialMode ? "Learning Mode" : "Learn Sequence"}
                  </Button>
                </div>
              )}
              
              {/* Image Carousel */}
              {selectedLocation.images && selectedLocation.images.length > 0 ? (
                <div className="relative">
                  <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img 
                      src={currentImage?.imageUrl || selectedLocation.imageUrl || '/placeholder-location.png'} 
                      alt={currentImage?.caption || selectedLocation.name}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Tags overlay for current image with sequential mode support */}
                    {(() => {
                      const sortedTags = [...currentImageTags].sort((a: any, b: any) => 
                        (a.displayOrder || 0) - (b.displayOrder || 0)
                      );
                      
                      return sortedTags.map((tag: any, index: number) => {
                        const isNextInSequence = sequentialMode && index === currentSequenceIndex;
                        const isCompleted = sequentialMode && index < currentSequenceIndex;
                        const isShaking = wrongTagShake === index;
                        
                        return (
                          <div
                            key={tag.id}
                            className={`absolute flex flex-col items-center cursor-pointer group z-10 transition-all duration-200 ${
                              isShaking ? 'animate-pulse' : ''
                            }`}
                            style={{ 
                              left: `${tag.x}%`, 
                              top: `${tag.y}%`,
                              transform: 'translate(-50%, -50%)',
                              opacity: sequentialMode && !isNextInSequence && !isCompleted ? 0.5 : 1
                            }}
                            onClick={() => {
                              if (sequentialMode) {
                                if (index === currentSequenceIndex) {
                                  // Play recorded audio or fallback to TTS
                                  playTagAudio(tag);
                                  
                                  const nextIndex = currentSequenceIndex + 1;
                                  setCurrentSequenceIndex(nextIndex);
                                  
                                  if (nextIndex === sortedTags.length) {
                                    setTimeout(() => {
                                      if ('speechSynthesis' in window) {
                                        const utterance = new SpeechSynthesisUtterance("Great job! You found all the tags!");
                                        window.speechSynthesis.speak(utterance);
                                      }
                                      playSound('success');
                                    }, 500);
                                  } else {
                                    setTimeout(() => {
                                      if ('speechSynthesis' in window) {
                                        const nextTag = sortedTags[nextIndex];
                                        const utterance = new SpeechSynthesisUtterance(`Next: ${nextTag.label}`);
                                        window.speechSynthesis.speak(utterance);
                                      }
                                    }, 800);
                                  }
                                } else {
                                  setWrongTagShake(index);
                                  setTimeout(() => setWrongTagShake(null), 500);
                                  playSound('error');
                                }
                              } else {
                                // Normal mode: Play recorded audio or fallback to TTS
                                playTagAudio(tag);
                              }
                            }}
                            title={tag.description || tag.label}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg border-2 transition-all duration-300 ${
                              isNextInSequence 
                                ? 'bg-green-500 border-green-300 ring-4 ring-green-300 ring-opacity-75 animate-pulse scale-125' 
                                : isCompleted 
                                  ? 'bg-gray-400 border-gray-300' 
                                  : 'bg-purple-600 border-white hover:bg-purple-700 hover:scale-110'
                            }`}>
                              {isCompleted ? '✓' : index + 1}
                            </div>
                            {showLabels && (
                              <div className={`mt-1 text-white text-sm font-semibold px-3 py-1 rounded-lg shadow-lg whitespace-nowrap max-w-[150px] truncate ${
                                isNextInSequence 
                                  ? 'bg-green-500' 
                                  : isCompleted 
                                    ? 'bg-gray-400' 
                                    : 'bg-purple-600'
                              }`}>
                                {tag.label}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                  
                  {/* Navigation buttons */}
                  {selectedLocation.images.length > 1 && (
                    <>
                      <Button
                        variant="outline"
                        size="icon"
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
                        onClick={prevImage}
                        disabled={currentImageIndex === 0}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
                        onClick={nextImage}
                        disabled={currentImageIndex === selectedLocation.images.length - 1}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  
                  {/* Image counter */}
                  {selectedLocation.images.length > 1 && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {selectedLocation.images.length}
                    </div>
                  )}
                  
                  {/* Caption */}
                  {currentImage?.caption && (
                    <p className="text-center text-gray-700 mt-2 text-sm italic">
                      {currentImage.caption}
                    </p>
                  )}
                </div>
              ) : selectedLocation.imageUrl ? (
                <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src={selectedLocation.imageUrl} 
                    alt={selectedLocation.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : null}
              
              {/* Location info */}
              <div className="space-y-3">
                {selectedLocation.subcategory && (
                  <Badge className="bg-blue-100 text-blue-800">{selectedLocation.subcategory}</Badge>
                )}
                
                {selectedLocation.description && (
                  <p className="text-gray-700">{selectedLocation.description}</p>
                )}
                
                {selectedLocation.address && (
                  <div className="flex items-start gap-2 text-gray-600">
                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p className="text-sm">
                      {selectedLocation.address}
                      {selectedLocation.city && `, ${selectedLocation.city}`}
                      {selectedLocation.state && `, ${selectedLocation.state}`}
                      {selectedLocation.zipCode && ` ${selectedLocation.zipCode}`}
                    </p>
                  </div>
                )}
              </div>
              
              {/* All tags list (if any) */}
              {selectedLocation.tags && selectedLocation.tags.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-800 mb-2">Things to Look For</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedLocation.tags.map((tag: any) => (
                      <Badge 
                        key={tag.id} 
                        variant="outline" 
                        className="cursor-pointer hover:bg-blue-50"
                        title={tag.description || ''}
                      >
                        {tag.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Choice Board Dialog */}
      <Dialog open={showChoiceBoardDialog} onOpenChange={(open) => {
        setShowChoiceBoardDialog(open);
        if (!open && linkedFromSchedule) {
          setLinkedFromSchedule(false);
          setLinkedScheduleStepId(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid3X3 className="h-5 w-5 text-purple-600" />
              {selectedChoiceBoard?.title || 'Make a Choice'}
            </DialogTitle>
          </DialogHeader>
          
          {loadingChoiceItems ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedChoiceBoard?.description && (
                <p className="text-gray-600">{selectedChoiceBoard.description}</p>
              )}
              
              {/* Selection instructions */}
              {needsConfirmation && !confirmationSent && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                  <p className="text-purple-700 font-medium">
                    Select {selectedChoiceBoard?.maxChoicesAllowed > 1 
                      ? `up to ${selectedChoiceBoard.maxChoicesAllowed} choices` 
                      : 'your choice'} then tap Confirm
                  </p>
                  {selectedChoiceItemIds.length > 0 && (
                    <p className="text-purple-600 text-sm mt-1">
                      {selectedChoiceItemIds.length} of {selectedChoiceBoard?.maxChoicesAllowed || 1} selected
                    </p>
                  )}
                </div>
              )}
              
              {choiceSelectionError && (
                <p role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {choiceSelectionError}
                </p>
              )}

              {choiceItems.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {choiceItems.map((item: any) => {
                      const isSelected = selectedChoiceItemIds.includes(item.id);
                      const maxReached = selectedChoiceItemIds.length >= (selectedChoiceBoard?.maxChoicesAllowed || 1);
                      const isDisabled = confirmationSent || (maxReached && !isSelected);
                      
                      return (
                      <Card 
                        key={item.id} 
                        className={`cursor-pointer transition-all ${
                          isSelected 
                            ? 'ring-4 ring-green-500 bg-green-50 scale-105' 
                            : 'hover:ring-2 hover:ring-purple-400'
                        } ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}
                        onClick={() => handleSelectChoice(item)}
                      >
                        <CardContent className="p-3 flex flex-col items-center text-center relative">
                          {isSelected && confirmationSent && (
                            <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 rounded-lg z-10">
                              <div className="bg-green-500 text-white rounded-full p-3">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                          )}
                          {isSelected && !confirmationSent && (
                            <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center z-10">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          )}
                          {item.mediaType === 'video' && item.videoUrl ? (
                            <video
                              src={item.videoUrl}
                              controls
                              preload="metadata"
                              className="w-full h-24 object-cover rounded-lg mb-2"
                              aria-label={`${item.title} video`}
                              onClick={(event) => event.stopPropagation()}
                            />
                          ) : item.imageUrl ? (
                            <img 
                              src={item.imageUrl} 
                              alt={item.title} 
                              className="w-full h-24 object-cover rounded-lg mb-2"
                            />
                          ) : (
                            <div className="w-full h-24 bg-purple-100 rounded-lg flex items-center justify-center mb-2">
                              <span className="text-4xl">🎯</span>
                            </div>
                          )}
                          <p className={`font-medium text-sm ${isSelected ? 'text-green-700' : 'text-gray-800'}`}>{item.title}</p>
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                          )}
                          {isSelected ? (
                            <div className="mt-1">
                              <span className="text-xs font-semibold text-green-600">
                                {confirmationSent ? 'Sent!' : 'Selected'}
                              </span>
                            </div>
                          ) : item.audioUrl && (
                            <div className="mt-1">
                              <span className="text-xs text-purple-500">🔊 Has audio</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                    })}
                  </div>
                  
                  {/* Confirm Button - only shown when needed */}
                  {needsConfirmation && selectedChoiceItemIds.length > 0 && !confirmationSent && (
                    <div className="flex justify-center pt-4">
                      <button
                        onClick={handleConfirmSelections}
                        disabled={selectionPending}
                        className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-xl text-xl shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {selectionPending ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Confirm Choice{selectedChoiceItemIds.length > 1 ? 's' : ''}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  
                  {/* Confirmation success message */}
                  {confirmationSent && (
                    <div className="flex justify-center pt-4">
                      <div className="bg-green-100 border border-green-300 text-green-700 rounded-xl py-4 px-8 text-xl font-bold flex items-center gap-2">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {linkedFromSchedule ? 'Choice Selected!' : 'Choice Sent!'}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                    <Grid3X3 className="h-6 w-6" />
                  </div>
                  <p>No choices available for this board.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Timer completion overlay — same UX as hub view ──────────────────── */}
      {completionOverlay && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">All Done!</h2>
            <p className="text-gray-600 mb-6">{completionOverlay.timerTitle} has finished</p>
            <div className="space-y-3">
              {isValidTimerCompletionResource(
                completionOverlay.completionResourceType,
                completionOverlay.completionResourceId,
              ) && (
                <Button className="w-full" onClick={openTimerCompletionResource}>
                  {completionOverlay.completionResourceType === 'schedule'
                    ? '📋 Open Schedule'
                    : '📖 Open Story'}
                </Button>
              )}
              <Button
                className="w-full"
                variant="ghost"
                onClick={() => {
                  acknowledgeTimerCompletion(completionOverlay.completionKey);
                  setCompletionOverlay(null);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Picture Schedule completion is shown only after the server has
          committed the final step and completed the active run. */}
      {scheduleCompletionOverlay && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Congratulations!</h2>
            <p className="text-gray-600 mb-6">{scheduleCompletionOverlay.scheduleTitle} is complete.</p>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => {
                setScheduleCompletionOverlay(null);
                setSelectedSchedule(null);
              }}
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {/* ── I Need a Break Button (always visible to care recipient) ── */}
      <BreakButton hubId={hubId} />

      {/* Care Mode full-screen viewer */}
      {careMode && careModeType && (
        <CareModeViewer
          type={careModeType}
          title={
            careModeType === 'schedule' ? (selectedSchedule?.title || 'Schedule') :
            careModeType === 'narrative' ? (selectedNarrative?.title || 'Story') :
            (selectedChoiceBoard?.title || 'Make a Choice')
          }
          // Schedule props
          schedule={selectedSchedule}
          currentStepIndex={currentStepIndex}
          onStepChange={setCurrentStepIndex}
          completedStepIds={completedStepIds}
          onToggleStep={toggleStepComplete}
          stepChoices={stepChoices}
          onOpenLinkedChoiceBoard={(boardId, stepKey) => openLinkedChoiceBoard(boardId, stepKey)}
          // Narrative props
          narrative={selectedNarrative}
          currentPageIndex={currentNarrativePageIndex}
          onPageChange={setCurrentNarrativePageIndex}
          // Choice board props
          choiceBoard={selectedChoiceBoard}
          choiceItems={choiceItems}
          loadingChoiceItems={loadingChoiceItems}
          selectedChoiceItemIds={selectedChoiceItemIds}
          onChoiceSelect={(id) => handleToggleChoice({ id, title: choiceItems.find((i: any) => i.id === id)?.title || '' })}
          onConfirmChoices={handleConfirmSelections}
          confirmationSent={confirmationSent}
          needsConfirmation={needsConfirmation}
          onClose={closeCareMode}
        />
      )}
    </div>
  );
}
